// Presentation helpers for Membership Plans — tier labels, duration
// humanisation, benefit-count derivation, gradient lookups.
//
// Kept dependency-free (no React, no @bbpos/ui) so the helpers can be
// unit-tested in isolation and reused anywhere.

import type { MembershipPlan, MembershipTier } from './types';

/**
 * Humanise a duration in days. Rounds to the nearest common bucket so the
 * UI never shows "29 days" or "730 days". Returns a pair: short (for pills)
 * and long (for hero subtitles).
 */
export function formatDuration(days: number): { short: string; long: string } {
  if (!Number.isFinite(days) || days <= 0) return { short: '—', long: '—' };
  if (days === 1) return { short: '1 day', long: '1 day' };
  if (days < 30) return { short: `${days} days`, long: `${days} days` };
  // 30-day buckets → months, with the one-year and multi-year shortcuts.
  if (days < 60) return { short: '1 month', long: '1 month' };
  if (days < 365) {
    const months = Math.round(days / 30);
    return { short: `${months} months`, long: `${months} months` };
  }
  if (days === 365) return { short: '1 year', long: '1 year' };
  if (days < 730) {
    const months = Math.round(days / 30);
    return { short: `${months} months`, long: `${months} months (~1 year)` };
  }
  const years = Math.round((days / 365) * 10) / 10;
  const yearLabel = years % 1 === 0 ? `${years.toFixed(0)} years` : `${years.toFixed(1)} years`;
  return { short: yearLabel, long: `${yearLabel} (${days} days)` };
}

/**
 * Human-friendly tier label. SILVER/GOLD/PLATINUM keep Title case;
 * CUSTOM renders as "Custom Tier".
 */
export function formatTierLabel(tier: MembershipTier): string {
  switch (tier) {
    case 'SILVER':
      return 'Silver';
    case 'GOLD':
      return 'Gold';
    case 'PLATINUM':
      return 'Platinum';
    case 'CUSTOM':
      return 'Custom';
  }
}

/**
 * Tailwind gradient classname per tier — used by the tier pill, preview
 * card, and list row accents. Gold leans warm amber, Platinum leans cool
 * indigo/violet, Silver leans cool slate, Custom gets a neutral brand tone
 * so admins can distinguish their bespoke plans at a glance.
 */
export function tierGradient(tier: MembershipTier): string {
  switch (tier) {
    case 'SILVER':
      return 'from-slate-300 via-zinc-400 to-slate-500';
    case 'GOLD':
      return 'from-amber-400 via-yellow-500 to-orange-500';
    case 'PLATINUM':
      return 'from-indigo-400 via-violet-500 to-fuchsia-500';
    case 'CUSTOM':
      return 'from-brand-500 via-indigo-500 to-violet-500';
  }
}

/**
 * Foreground shadow colour for the preview card — matches the tier gradient
 * so each card feels like it was sculpted from that metal.
 */
export function tierShadow(tier: MembershipTier): string {
  switch (tier) {
    case 'SILVER':
      return 'shadow-[0_25px_70px_-25px_rgba(100,116,139,0.65)]';
    case 'GOLD':
      return 'shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]';
    case 'PLATINUM':
      return 'shadow-[0_25px_70px_-25px_rgba(139,92,246,0.65)]';
    case 'CUSTOM':
      return 'shadow-[0_25px_70px_-25px_rgba(99,102,241,0.55)]';
  }
}

/**
 * Count the non-zero benefits on a plan. The backend has fixed benefit
 * fields rather than a benefits array, so we derive the count from whatever
 * is non-default. Used in list rows and the preview card chip.
 */
export function countBenefits(plan: Pick<MembershipPlan, 'discount_percentage' | 'points_multiplier' | 'priority_service' | 'auto_renewal'>): number {
  let n = 0;
  if (plan.discount_percentage > 0) n++;
  if (plan.points_multiplier > 1) n++;
  if (plan.priority_service) n++;
  if (plan.auto_renewal) n++;
  return n;
}

/**
 * Generate an ordered array of benefit labels for the preview card + detail
 * page benefits list. Consistent ordering regardless of how flags were set.
 */
export function listBenefits(plan: Pick<MembershipPlan, 'discount_percentage' | 'points_multiplier' | 'priority_service' | 'auto_renewal'>): { icon: 'discount' | 'points' | 'priority' | 'renewal'; label: string }[] {
  const out: { icon: 'discount' | 'points' | 'priority' | 'renewal'; label: string }[] = [];
  if (plan.discount_percentage > 0) {
    out.push({ icon: 'discount', label: `${plan.discount_percentage}% off every bill` });
  }
  if (plan.points_multiplier > 1) {
    out.push({ icon: 'points', label: `${plan.points_multiplier}× loyalty points` });
  }
  if (plan.priority_service) {
    out.push({ icon: 'priority', label: 'Priority service at checkout' });
  }
  if (plan.auto_renewal) {
    out.push({ icon: 'renewal', label: 'Auto-renewal enabled' });
  }
  return out;
}
